/* eslint-disable class-methods-use-this */
import React from 'react';
import { Row, Col } from 'react-bootstrap';

import Aux from "../../hoc/_Aux/Aux";
import '../../styles/scss/style.scss';

class PrivacyPolicy extends React.Component {
  render() {
    return (
      <Aux>
        <Row>
          <Col>
            <div className="card-body">

              <h1>Privacy Policy — Bailo.app</h1>
              <p><em>Last updated: July 2026</em></p>
              <p>This Privacy Policy describes how Bailo.app collects, uses, and protects your information when you use our Service, and explains your rights under applicable privacy laws including the EU General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA/CPRA), Canada's PIPEDA, and Australia's Privacy Act 2024.</p>

              <h2>1. Definitions</h2>
              <ul>
                <li><p><strong>Company</strong> ("we", "us", "our") refers to Bailo.app.</p></li>
                <li><p><strong>Service</strong> means the Bailo.app mobile application (iOS and Android), the website at <a href="https://bailo.app" rel="noreferrer external nofollow noopener" target="_blank">https://bailo.app</a>, and any related platforms.</p></li>
                <li><p><strong>App User</strong> means any person who uses the mobile app or website to browse dance-related content.</p></li>
                <li><p><strong>Organizer</strong> means any person or entity registered on the website platform to publish venue or event listings.</p></li>
                <li><p><strong>Anonymous Profile</strong> means the lightweight user profile created on first use of the mobile app, identified by a Device Installation ID.</p></li>
                <li><p><strong>Device Installation ID</strong> means a unique identifier (UUID) generated by the app on first launch and stored on the user's device, used to associate preferences, likes, and notifications with that device.</p></li>
                <li><p><strong>Personal Data</strong> means any information that relates to an identified or identifiable individual.</p></li>
                <li><p><strong>You</strong> means the individual or entity using the Service.</p></li>
              </ul>

              <h2>2. Who We Are and Data Controller</h2>
              <p>Bailo.app is the Data Controller for the purposes of the GDPR and other applicable privacy laws.</p>
              <ul>
                <li>Contact: <a href="mailto:bailo.app@gmail.com">bailo.app@gmail.com</a></li>
                <li>Website: <a href="https://bailo.app" rel="noreferrer external nofollow noopener" target="_blank">https://bailo.app</a></li>
                <li>Country of establishment: Spain</li>
              </ul>

              <h2>3. What Data We Collect</h2>
              <h3>3.1 App Users — Anonymous Profile</h3>
              <p>When you first open the mobile app, we collect:</p>
              <table className="table table-bordered table-sm" style={{ fontSize: 13, marginBottom: 16 }}>
                <thead className="thead-light">
                  <tr><th>Data</th><th>Required</th><th>Purpose</th></tr>
                </thead>
                <tbody>
                  <tr><td>Device Installation ID (UUID)</td><td>Yes</td><td>Identify your device across sessions</td></tr>
                  <tr><td>Display name</td><td>Yes</td><td>Personalise notifications and in-app experience</td></tr>
                  <tr><td>Email address</td><td>No (optional)</td><td>Recover your profile if you change devices</td></tr>
                  <tr><td>City and country</td><td>No (inferred or entered)</td><td>Send location-relevant notifications</td></tr>
                  <tr><td>GPS coordinates (latitude/longitude)</td><td>Only if you grant location permission</td><td>Determine your city and country to show you relevant events nearby</td></tr>
                  <tr><td>Push notification token (FCM/APNs)</td><td>Only if you grant permission</td><td>Deliver push notifications to your device</td></tr>
                  <tr><td>Terms acceptance timestamp</td><td>Yes</td><td>Legal compliance record</td></tr>
                  <tr><td>Last seen timestamp</td><td>Yes</td><td>Detect inactive profiles for data hygiene</td></tr>
                  <tr><td>Likes (venue/event IDs)</td><td>Yes, when you like something</td><td>Send relevant notifications; power analytics</td></tr>
                </tbody>
              </table>
              <p>We do <strong>not</strong> collect:</p>
              <ul>
                <li>Your real name (unless you choose it as your display name).</li>
                <li>Your phone number.</li>
                <li>Payment information.</li>
                <li>Any advertising identifiers (IDFA, GAID, Android ID).</li>
                <li>Your contacts, messages, photos, or any data unrelated to tango events.</li>
              </ul>
              <h3>3.1.1 Location Data</h3>
              <p>When you grant location permission, Bailo.app accesses your device's GPS coordinates (latitude and longitude) solely to identify your approximate city and country, so we can show you tango events relevant to your location. Your GPS coordinates are transmitted to our servers, used to perform a reverse geocoding lookup (via HERE Maps), and stored in association with your device record. We do not use your location for advertising, profiling, or any purpose other than showing nearby events. We do not share your GPS coordinates with third parties other than our geocoding provider (HERE Maps) for the reverse geocoding step. You may revoke location permission at any time in your device settings, in which case your city and country will remain as last detected or can be set manually in the app.</p>
              <h3>3.2 Organizers</h3>
              <p>When you register as an Organizer, we collect:</p>
              <table className="table table-bordered table-sm" style={{ fontSize: 13, marginBottom: 16 }}>
                <thead className="thead-light">
                  <tr><th>Data</th><th>Purpose</th></tr>
                </thead>
                <tbody>
                  <tr><td>Email address</td><td>Account authentication and communication</td></tr>
                  <tr><td>Password (hashed)</td><td>Account security</td></tr>
                  <tr><td>Venue/event information</td><td>Publishing your listings on the Service</td></tr>
                  <tr><td>Account activity logs</td><td>Security and fraud prevention</td></tr>
                </tbody>
              </table>
              <h3>3.3 Usage and Analytics Data</h3>
              <p>For all users, we collect anonymised usage data including:</p>
              <ul>
                <li>Type of device and operating system.</li>
                <li>Pages and sections visited.</li>
                <li>Interactions with venue and event listings (views, description reads, contact clicks).</li>
                <li>Time and date of activity.</li>
              </ul>
              <p>This data is collected at the aggregate level and is not linked to personally identifiable information.</p>

              <h2>4. How We Use Your Data</h2>
              <h3>App Users</h3>
              <ul>
                <li>To create and maintain your Anonymous Profile.</li>
                <li>To send you push notifications about venues and events you have liked.</li>
                <li>To personalise your experience based on your location.</li>
                <li>To maintain records of your likes and notification history.</li>
                <li>To detect and deactivate inactive profiles (data hygiene).</li>
                <li>To comply with legal obligations.</li>
              </ul>
              <h3>Organizers</h3>
              <ul>
                <li>To provide and manage your account on the platform.</li>
                <li>To display your published Content to App Users.</li>
                <li>To provide you with analytics about your listings.</li>
                <li>To send you service-related communications.</li>
                <li>To comply with legal obligations.</li>
              </ul>
              <h3>All Users</h3>
              <ul>
                <li>To improve and maintain the Service.</li>
                <li>To detect and prevent fraud and abuse.</li>
                <li>To comply with legal obligations and enforce our Terms.</li>
              </ul>

              <h2>5. Legal Basis for Processing (GDPR)</h2>
              <p>For users in the European Economic Area, we process your data under the following legal bases:</p>
              <table className="table table-bordered table-sm" style={{ fontSize: 13, marginBottom: 16 }}>
                <thead className="thead-light">
                  <tr><th>Processing activity</th><th>Legal basis</th></tr>
                </thead>
                <tbody>
                  <tr><td>Creating an Anonymous Profile</td><td>Consent (given at onboarding)</td></tr>
                  <tr><td>Sending push notifications</td><td>Consent (notification permission granted)</td></tr>
                  <tr><td>Maintaining likes and preferences</td><td>Consent</td></tr>
                  <tr><td>Organizer account management</td><td>Performance of contract</td></tr>
                  <tr><td>Usage analytics (anonymised)</td><td>Legitimate interests</td></tr>
                  <tr><td>Legal compliance</td><td>Legal obligation</td></tr>
                  <tr><td>Fraud prevention</td><td>Legitimate interests</td></tr>
                </tbody>
              </table>
              <p>You may withdraw your consent at any time. For push notifications, this can be done through your device settings. For your Anonymous Profile, you can request deletion through the app's Settings section.</p>

              <h2>6. Data Retention</h2>
              <table className="table table-bordered table-sm" style={{ fontSize: 13, marginBottom: 16 }}>
                <thead className="thead-light">
                  <tr><th>Data type</th><th>Retention period</th></tr>
                </thead>
                <tbody>
                  <tr><td>Anonymous Profile (active)</td><td>Until you request deletion or profile becomes stale</td></tr>
                  <tr><td>Anonymous Profile (inactive)</td><td>Marked as stale after 12 months of inactivity; excluded from notifications</td></tr>
                  <tr><td>Anonymous Profile (deleted by user)</td><td>Removed from active systems within 30 days; purged from backups within 6 months</td></tr>
                  <tr><td>Push notification tokens</td><td>Removed immediately upon detection of token invalidation or account deletion</td></tr>
                  <tr><td>Likes and notification history</td><td>Retained while profile is active; deleted with the profile</td></tr>
                  <tr><td>Organizer account data</td><td>Retained while account is active; deleted upon account closure</td></tr>
                  <tr><td>Anonymised usage analytics</td><td>Up to 24 months</td></tr>
                </tbody>
              </table>
              <p><strong>Profile considered stale:</strong> If you do not open the app for 12 consecutive months, your profile is marked as stale and excluded from all push notifications. Your data remains in our systems but is inactive. If you reinstall the app, your profile is reactivated automatically.</p>

              <h2>7. Push Notifications and Token Management</h2>
              <p>We use Firebase Cloud Messaging (FCM) by Google for Android and Apple Push Notification Service (APNs) for iOS to deliver push notifications.</p>
              <p>Your push token is updated automatically each time you open the app. If a notification cannot be delivered (for example, because you have uninstalled the app), the token is immediately invalidated in our systems and your profile is marked as stale.</p>
              <p>We may send silent background pings (invisible to you) to devices that have been inactive for more than 30 days to detect whether the push token is still valid. This helps us maintain accurate notification delivery.</p>

              <h2>8. Sharing Your Data</h2>
              <p>We do <strong>not</strong> sell your personal data to third parties.</p>
              <p>We may share data in the following limited circumstances:</p>
              <p><strong>Service providers:</strong> We use the following third-party processors to operate the Service:</p>
              <ul>
                <li><strong>Google Firebase (FCM):</strong> push notification delivery. Google's DPA is in place.</li>
                <li><strong>Expo (optional):</strong> push notification infrastructure. Expo's DPA is in place.</li>
                <li><strong>HERE Maps:</strong> reverse geocoding (converting GPS coordinates to city/country). Used transiently; no data retained by HERE beyond the lookup request.</li>
                <li><strong>MongoDB Atlas:</strong> database hosting.</li>
                <li><strong>Render / Netlify:</strong> infrastructure hosting.</li>
              </ul>
              <p>All service providers are bound by data processing agreements and may only use your data to provide services to us.</p>
              <p><strong>Legal requirements:</strong> We may disclose your data if required by law, court order, or a competent public authority.</p>
              <p><strong>Business transfers:</strong> If Bailo.app is involved in a merger or acquisition, your data may be transferred. We will notify you before your data becomes subject to a different privacy policy.</p>

              <h2>9. Your Rights</h2>
              <h3>EU / EEA Users (GDPR)</h3>
              <p>You have the right to:</p>
              <ul>
                <li>Access your personal data.</li>
                <li>Rectify inaccurate data.</li>
                <li>Erase your data ("right to be forgotten").</li>
                <li>Restrict processing of your data.</li>
                <li>Data portability (receive your data in a machine-readable format).</li>
                <li>Object to processing based on legitimate interests.</li>
                <li>Withdraw consent at any time.</li>
                <li>Lodge a complaint with your local Data Protection Authority (in Spain: AEPD — <a href="https://www.aepd.es" rel="noreferrer external nofollow noopener" target="_blank">www.aepd.es</a>).</li>
              </ul>
              <h3>California Users (CCPA/CPRA)</h3>
              <p>You have the right to:</p>
              <ul>
                <li>Know what personal data we collect and how we use it.</li>
                <li>Delete your personal data.</li>
                <li>Opt out of the sale of personal data (we do not sell personal data).</li>
                <li>Non-discrimination for exercising your rights.</li>
              </ul>
              <h3>Canadian Users (PIPEDA)</h3>
              <p>You have the right to access and correct your personal data, and to withdraw consent for its use.</p>
              <h3>Australian Users (Privacy Act 2024)</h3>
              <p>You have the right to access, correct, and request deletion of your personal data.</p>
              <p>To exercise any of these rights, contact us at <a href="mailto:bailo.app@gmail.com">bailo.app@gmail.com</a>. We will respond within 30 days. We may ask you to verify your identity before processing your request.</p>

              <h2>10. Cookies and Tracking Technologies</h2>
              <table className="table table-bordered table-sm" style={{ fontSize: 13, marginBottom: 16 }}>
                <thead className="thead-light">
                  <tr><th>Cookie type</th><th>Purpose</th><th>Can be disabled?</th></tr>
                </thead>
                <tbody>
                  <tr><td>Essential / Session</td><td>Authentication and core functionality</td><td>No (required for the service to work)</td></tr>
                  <tr><td>Preference</td><td>Remember your language and settings</td><td>Yes</td></tr>
                  <tr><td>Analytics (anonymised)</td><td>Understand how the Service is used</td><td>Yes</td></tr>
                </tbody>
              </table>
              <p>We do not use advertising cookies or third-party tracking for marketing purposes.</p>
              <p>You can manage cookie preferences through your browser settings at any time.</p>

              <h2>11. Children's Privacy</h2>
              <p>The Service is not directed at individuals under the age of 18. We do not knowingly collect personal data from minors. If you believe a minor has provided us with personal data, please contact us immediately at <a href="mailto:bailo.app@gmail.com">bailo.app@gmail.com</a> and we will take steps to delete it.</p>
              <p><strong>Child Safety:</strong> Bailo.app has a zero-tolerance policy for any content that exploits or harms minors. Any such content will be removed immediately and reported to relevant national authorities and the National Center for Missing &amp; Exploited Children (NCMEC). To report a violation, email <a href="mailto:bailo.app@gmail.com">bailo.app@gmail.com</a> with subject line "CSAM Report".</p>

              <h2>12. Data Security</h2>
              <p>We implement appropriate technical and organisational measures to protect your data, including:</p>
              <ul>
                <li>Encryption of data in transit (HTTPS/TLS).</li>
                <li>Secure storage of push tokens and installation IDs.</li>
                <li>Hashing of Organizer passwords.</li>
                <li>Regular security reviews.</li>
                <li>Restricted access to personal data on a need-to-know basis.</li>
              </ul>
              <p>No method of transmission over the Internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.</p>

              <h2>13. International Data Transfers</h2>
              <p>Your data may be processed in countries outside your own, including the United States (where Google Firebase and MongoDB Atlas servers may be located). Where data is transferred outside the EEA, we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) approved by the European Commission.</p>

              <h2>14. Data Hygiene and Inactive Profiles</h2>
              <p>To keep our systems clean and respect your privacy, we operate the following automated data hygiene processes:</p>
              <ul>
                <li>Profiles inactive for more than 12 months are marked as stale and excluded from notifications.</li>
                <li>Profiles where the user has requested deletion are removed from active systems within 30 days and from backups within 6 months.</li>
                <li>Push tokens that return delivery errors are invalidated immediately.</li>
              </ul>

              <h2>15. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. If changes are significant, we will notify you through the app or by email (if provided) at least 30 days before the changes take effect. The updated date at the top of this document reflects the most recent version.</p>

              <h2>16. Contact and Complaints</h2>
              <p>For privacy requests or questions:<br />Email: <a href="mailto:bailo.app@gmail.com">bailo.app@gmail.com</a></p>
              <p>For EU users — supervisory authority:<br />
                Agencia Española de Protección de Datos (AEPD)<br />
                <a href="https://www.aepd.es" rel="noreferrer external nofollow noopener" target="_blank">www.aepd.es</a>
              </p>
              <p>For users in other jurisdictions, you may contact your local data protection authority.</p>

            </div>
          </Col>
        </Row>
      </Aux>
    );
  }
}

export default PrivacyPolicy;
